/**
 * Action Selector Component
 * Multi-select dropdown for choosing integration actions
 */

import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { ActionSelectorProps } from "./types";

/**
 * ActionSelector component for multi-selecting integration actions
 *
 * @param integrationId - The ID of the integration
 * @param availableActions - Array of available actions
 * @param selectedActions - Array of currently selected action IDs
 * @param onActionsChange - Callback when selection changes
 * @param disabled - Whether the selector is disabled
 * @param className - Additional CSS classes
 */
export function ActionSelector({
  availableActions,
  selectedActions,
  onActionsChange,
  disabled = false,
  className,
}: ActionSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);

  /**
   * Toggle action selection
   * Adds or removes action from current selection
   */
  const toggleAction = (actionId: string) => {
    if (disabled) return;

    const newSelection = selectedActions.includes(actionId)
      ? selectedActions.filter((id) => id !== actionId)
      : [...selectedActions, actionId];

    onActionsChange(newSelection);
  };

  /**
   * Get display text for selected actions
   * Returns human-readable summary of selection state
   */
  const getDisplayText = () => {
    if (selectedActions.length === 0) {
      return "Select actions...";
    }

    if (selectedActions.length === 1) {
      const action = availableActions.find((a) => a.id === selectedActions[0]);
      return action?.name || "Unknown action";
    }

    return `${selectedActions.length} actions selected`;
  };

  // If no actions available, show disabled state
  if (availableActions.length === 0) {
    return (
      <div className={cn("text-sm text-muted-foreground", className)}>
        No actions available
      </div>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            className={cn(
              "w-full justify-between text-left font-normal",
              selectedActions.length === 0 && "text-muted-foreground"
            )}
          >
            <span className="truncate">{getDisplayText()}</span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-full p-0" align="start">
          <div className="max-h-60 overflow-auto">
            {availableActions.map((action) => (
              <div
                key={action.id}
                className={cn(
                  "flex items-center space-x-2 p-3 cursor-pointer hover:bg-muted",
                  action.available === false && "opacity-50 cursor-not-allowed"
                )}
                onClick={() =>
                  action.available !== false && toggleAction(action.id)
                }
              >
                <div
                  className={cn(
                    "h-4 w-4 border rounded-sm flex items-center justify-center",
                    selectedActions.includes(action.id)
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-input"
                  )}
                >
                  {selectedActions.includes(action.id) && (
                    <Check className="h-3 w-3" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{action.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {action.description}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {selectedActions.length > 0 && (
            <div className="border-t p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onActionsChange([])}
                className="w-full text-xs"
              >
                Clear all selections
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
